import { z } from 'zod';

// Cross-field checks only run once day, month and year are individually valid, otherwise
// a range error in one part would also surface a misleading "Invalid date" / "underage".
const hasNoPriorIssues = (payload: z.core.ParsePayload) => payload.issues.length === 0;

// Modified to work better with React Hook Form and TypeScript
const DateOfBirthSchema = (t: (key: string) => string) =>
  z
    .object({
      day: z
        .number({ error: t('validation.dateOfBirth.day.required') })
        .min(1, t('validation.dateOfBirth.day.range'))
        .max(31, t('validation.dateOfBirth.day.range')),
      month: z
        .number({ error: t('validation.dateOfBirth.month.required') })
        .min(1, t('validation.dateOfBirth.month.range'))
        .max(12, t('validation.dateOfBirth.month.range')),
      year: z
        .number({ error: t('validation.dateOfBirth.year.required') })
        .min(1900, t('validation.dateOfBirth.year.min'))
        .max(new Date().getFullYear(), t('validation.dateOfBirth.future')),
    })
    .refine(
      data => {
        // Validate that the date actually exists
        const date = new Date(data.year, data.month - 1, data.day);
        return date.getFullYear() === data.year && date.getMonth() === data.month - 1 && date.getDate() === data.day;
      },
      {
        // No `path`: the error sits on the group, which DateOfBirth re-validates when any part changes
        message: t('validation.dateOfBirth.invalid'),
        when: hasNoPriorIssues,
      }
    )
    .refine(
      data => {
        // Ensure user is at least 18 years old
        const birthDate = new Date(data.year, data.month - 1, data.day);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        const dayDiff = today.getDate() - birthDate.getDate();

        // Calculate exact age accounting for month/day
        const exactAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
        return exactAge >= 18;
      },
      {
        message: t('validation.dateOfBirth.underage'),
        when: hasNoPriorIssues,
      }
    );

export { DateOfBirthSchema };
